/**
 * ==================== ONBOARDING TOUR COMPONENT ====================
 * Sistema de tour guiado usando react-joyride.
 * Módulo isolado - funciona como overlay sem modificar código existente.
 */
import React, { useState, useEffect, useCallback, useContext } from 'react';
import { Joyride, STATUS, ACTIONS, EVENTS } from 'react-joyride';
import axios from 'axios';
import { AuthContext, API } from '@/App';

// ==================== TOUR STEPS - CLIENTE ====================
const CLIENT_TOUR_STEPS = [
  {
    target: 'body',
    content: (
      <div className="text-center">
        <div className="text-4xl mb-3">🚗</div>
        <h3 className="text-lg font-bold mb-2">Bem-vindo à JATOO!</h3>
        <p className="text-gray-600">
          Aqui você agenda lavagens em lava-jatos próximos de forma prática e segura.
          Vamos te mostrar como funciona!
        </p>
      </div>
    ),
    placement: 'center',
    disableBeacon: true,
    hideSkipButton: true, // Fix: Skip button doesn't work on first step in react-joyride v3
  },
  {
    target: '[data-testid="plan-card"]',
    content: (
      <div>
        <h3 className="text-lg font-bold mb-2">💎 Seu Plano de Assinatura</h3>
        <p className="text-gray-600">
          Aqui você vê o seu plano atual, quantas lavagens ainda tem disponíveis 
          no mês e quando será a próxima cobrança.
        </p>
      </div>
    ),
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '[data-testid="schedule-main-btn"]',
    content: (
      <div>
        <h3 className="text-lg font-bold mb-2">📅 Agendar Lavagem</h3>
        <p className="text-gray-600">
          Clique aqui para encontrar lava-jatos próximos. Escolha o dia, 
          horário e tipo de serviço que preferir!
        </p>
      </div>
    ),
    placement: 'top',
    disableBeacon: true,
  },
  {
    target: 'body',
    content: (
      <div className="text-center">
        <div className="text-4xl mb-3">📋</div>
        <h3 className="text-lg font-bold mb-2">Acompanhe seus Agendamentos</h3>
        <p className="text-gray-600">
          Na seção "Seus Agendamentos" você vê todos os serviços: pendentes, 
          confirmados e em andamento. E no histórico, os serviços já concluídos.
        </p>
      </div>
    ),
    placement: 'center',
    disableBeacon: true,
  },
  {
    target: 'body',
    content: (
      <div className="text-center">
        <div className="text-4xl mb-3">⭐</div>
        <h3 className="text-lg font-bold mb-2">Avaliações dos Prestadores</h3>
        <p className="text-gray-600">
          Ao buscar um lava-jato, veja as avaliações de outros clientes! 
          Quanto mais estrelas, melhor o serviço. Suas avaliações também 
          ajudam outros motoristas!
        </p>
      </div>
    ),
    placement: 'center',
    disableBeacon: true,
  },
  {
    target: 'body',
    content: (
      <div className="text-center">
        <div className="text-4xl mb-3">🎉</div>
        <h3 className="text-lg font-bold mb-2">Tudo pronto!</h3>
        <p className="text-gray-600">
          Agora você já sabe como usar a JATOO. 
          Agende sua primeira lavagem e aproveite!
        </p>
      </div>
    ),
    placement: 'center',
    disableBeacon: true,
  },
];

// ==================== TOUR STEPS - PRESTADOR ====================
const PROVIDER_TOUR_STEPS = [
  {
    target: 'body',
    content: (
      <div className="text-center">
        <div className="text-4xl mb-3">🚗</div>
        <h3 className="text-lg font-bold mb-2">Bem-vindo à JATOO!</h3>
        <p className="text-gray-600">
          Aqui você receberá solicitações de lavagem de clientes próximos. 
          Vamos te mostrar como funciona a plataforma!
        </p>
      </div>
    ),
    placement: 'center',
    disableBeacon: true,
    hideSkipButton: true, // Fix: Skip button doesn't work on first step in react-joyride v3
  },
  {
    target: '[data-testid="availability-card"]',
    content: (
      <div>
        <h3 className="text-lg font-bold mb-2">🔔 Solicitações de Serviço</h3>
        <p className="text-gray-600">
          Quando um cliente agendar com você, a solicitação aparecerá na 
          tela de Serviços. Você tem <strong>10 minutos</strong> para aceitar!
        </p>
      </div>
    ),
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: 'body',
    content: (
      <div className="text-center">
        <div className="text-4xl mb-3">✅</div>
        <h3 className="text-lg font-bold mb-2">Aceitar ou Recusar</h3>
        <p className="text-gray-600">
          Analise os detalhes do serviço (cliente, veículo, data, horário e valor) 
          e decida se aceita ou recusa. <br/>
          <span className="text-orange-600 font-medium">
            Recusar muitos serviços pode afetar seu ranking!
          </span>
        </p>
      </div>
    ),
    placement: 'center',
    disableBeacon: true,
  },
  {
    target: 'body',
    content: (
      <div className="text-center">
        <div className="text-4xl mb-3">▶️</div>
        <h3 className="text-lg font-bold mb-2">Iniciar o Serviço</h3>
        <p className="text-gray-600">
          Quando o cliente chegar, clique em "Iniciar Serviço". 
          Isso registra o início da lavagem no sistema.
        </p>
      </div>
    ),
    placement: 'center',
    disableBeacon: true,
  },
  {
    target: 'body',
    content: (
      <div className="text-center">
        <div className="text-4xl mb-3">🔐</div>
        <h3 className="text-lg font-bold mb-2">Finalizar com Código</h3>
        <p className="text-gray-600">
          Ao terminar a lavagem, peça o <strong>código de confirmação</strong> ao cliente. 
          Esse código libera seu pagamento!
        </p>
      </div>
    ),
    placement: 'center',
    disableBeacon: true,
  },
  {
    target: 'body',
    content: (
      <div className="text-center">
        <div className="text-4xl mb-3">⭐</div>
        <h3 className="text-lg font-bold mb-2">Avaliações e Pontuação</h3>
        <p className="text-gray-600">
          Clientes podem avaliar seu serviço. Avaliações altas aumentam sua 
          visibilidade e atraem mais clientes!
        </p>
      </div>
    ),
    placement: 'center',
    disableBeacon: true,
  },
  {
    target: 'body',
    content: (
      <div className="text-center">
        <div className="text-4xl mb-3">⚠️</div>
        <h3 className="text-lg font-bold mb-2">Regras Importantes</h3>
        <div className="text-left bg-amber-50 p-3 rounded-lg text-sm">
          <p className="mb-2">• <strong>Cancelar serviços</strong> reduz seu ranking</p>
          <p className="mb-2">• <strong>Avaliações baixas</strong> diminuem sua visibilidade</p>
          <p>• <strong>Contestações</strong> podem bloquear repasses temporariamente</p>
        </div>
      </div>
    ),
    placement: 'center',
    disableBeacon: true,
  },
];

// ==================== CUSTOM TOOLTIP STYLES ====================
const tooltipStyles = {
  options: {
    zIndex: 10000,
    primaryColor: '#2563eb',
  },
  tooltip: {
    borderRadius: '12px',
    padding: '20px',
    maxWidth: '380px',
  },
  tooltipContainer: {
    textAlign: 'left',
  },
  tooltipTitle: {
    fontSize: '18px',
    fontWeight: '600',
  },
  tooltipContent: {
    fontSize: '14px',
    lineHeight: '1.6',
  },
  buttonNext: {
    backgroundColor: '#2563eb',
    borderRadius: '8px',
    padding: '10px 20px',
    fontSize: '14px',
    fontWeight: '500',
  },
  buttonBack: {
    color: '#6b7280',
    marginRight: '10px',
    fontSize: '14px',
  },
  buttonSkip: {
    color: '#9ca3af',
    fontSize: '13px',
  },
  spotlight: {
    borderRadius: '12px',
  },
  overlay: {
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    // v3 fix: overlay default sizing uses documentElement.scrollWidth/Height,
    // which can leak a white strip beyond the viewport on apps that have any
    // horizontal overflow. Force fixed positioning to the visible viewport.
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    width: '100vw',
    height: '100vh',
  },
};

// ==================== LOCALE (PT-BR) ====================
const locale = {
  back: 'Voltar',
  close: 'Fechar',
  last: 'Concluir',
  next: 'Próximo',
  open: 'Abrir',
  skip: 'Pular tutorial',
};

// ==================== MAIN COMPONENT ====================
export default function OnboardingTour({ userType, onComplete }) {
  const [run, setRun] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const { user } = useContext(AuthContext);

  const steps = userType === 'provider' ? PROVIDER_TOUR_STEPS : CLIENT_TOUR_STEPS;

  // ==================== [TELEMETRY - TEMPORARY, NO LOGIC CHANGE] ====================
  // Mount / unmount detection. Runs once per lifecycle.
  useEffect(() => {
    console.log('[TOUR-TELEMETRY] MOUNT', {
      userType,
      userId: user?.id,
      userEmail: user?.email,
      hasToken: !!localStorage.getItem('token'),
      href: typeof window !== 'undefined' ? window.location.href : null,
      ts: new Date().toISOString(),
    });
    return () => {
      console.log('[TOUR-TELEMETRY] UNMOUNT', {
        userType,
        userId: user?.id,
        ts: new Date().toISOString(),
      });
    };
  }, []);
  // ==================== [/TELEMETRY] ====================

  // Chave local para persistir o estado do tour por usuário. Usada como cache
  // rápido além do backend, evitando re-abertura do tour em cada navegação para
  // o dashboard (especialmente crítico no app Capacitor Android, onde o usuário
  // permanece autenticado por muito tempo e não deve rever o tour repetidamente).
  const tourStorageKey = React.useMemo(() => {
    const uid = user?.id || user?.email || 'anonymous';
    const key = `jatoo_tour_completed_${userType}_${uid}`;
    // [TOUR-TELEMETRY] Log the computed key so we can spot uid drift (e.g. anonymous vs real id)
    console.log('[TOUR-TELEMETRY] tourStorageKey computed', {
      key,
      uidSource: user?.id ? 'user.id' : (user?.email ? 'user.email' : 'anonymous'),
      ts: new Date().toISOString(),
    });
    return key;
  }, [user, userType]);

  // Verificar se deve mostrar o tour
  useEffect(() => {
    const checkTourStatus = async () => {
      // [TOUR-TELEMETRY] Snapshot inicial: valor do cache antes de qualquer ação
      const cacheBefore = typeof window !== 'undefined'
        ? window.localStorage.getItem(tourStorageKey)
        : null;
      console.log('[TOUR-TELEMETRY] checkTourStatus:start', {
        tourStorageKey,
        cacheBefore,
        cacheMatches: cacheBefore === '1',
        ts: new Date().toISOString(),
      });
      try {
        // 1) Cache local: se já concluiu neste device, não checa backend
        if (typeof window !== 'undefined' && window.localStorage.getItem(tourStorageKey) === '1') {
          console.log('[TOUR-TELEMETRY] checkTourStatus:cache-hit -> SHORT-CIRCUIT, no backend call, tour NOT opened', {
            tourStorageKey,
            ts: new Date().toISOString(),
          });
          setLoading(false);
          return;
        }
        const token = localStorage.getItem('token');
        if (!token) {
          console.log('[TOUR-TELEMETRY] checkTourStatus:no-token -> skipping backend, tour NOT opened');
          setLoading(false);
          return;
        }

        console.log('[TOUR-TELEMETRY] checkTourStatus:calling-backend GET /onboarding/tour-status');
        const res = await axios.get(`${API}/onboarding/tour-status`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        console.log('[TOUR-TELEMETRY] checkTourStatus:backend-response', {
          status: res.status,
          data: res.data,
          ts: new Date().toISOString(),
        });

        if (res.data.should_show_tour) {
          console.log('[TOUR-TELEMETRY] checkTourStatus:decision=OPEN_TOUR (backend said should_show_tour=true) -> setTimeout 500ms -> setRun(true)', {
            tourStorageKey,
            ts: new Date().toISOString(),
          });
          // ==================== [SURGICAL FIX — abertura automática única] ====================
          // Marcar imediatamente como "auto-exibido neste device" ANTES de disparar
          // o Joyride. Isso garante que, mesmo se o usuário sair do tour por um
          // caminho que NÃO dispara STATUS.FINISHED/SKIPPED/CLOSE do react-joyride
          // (navegação para outra rota, botão físico "voltar" do Android, kill do
          // app), a próxima montagem já encontrará cache='1' na linha 336 e
          // curto-circuitará sem consultar o backend — evitando a reabertura.
          try { window.localStorage.setItem(tourStorageKey, '1'); } catch (_) { /* ignore */ }
          // ==================== [/SURGICAL FIX] ====================
          // Pequeno delay para garantir que os elementos estejam renderizados
          setTimeout(() => {
            console.log('[TOUR-TELEMETRY] setRun(true) FIRED — TOUR IS OPENING NOW', {
              tourStorageKey,
              ts: new Date().toISOString(),
            });
            setRun(true);
          }, 500);
        } else {
          // Backend indica que já viu → propagar para o cache local
          console.log('[TOUR-TELEMETRY] checkTourStatus:decision=DO_NOT_OPEN (backend said should_show_tour=false) -> writing cache=1');
          const beforeWrite = window.localStorage.getItem(tourStorageKey);
          try { window.localStorage.setItem(tourStorageKey, '1'); } catch (e) {
            console.log('[TOUR-TELEMETRY] localStorage.setItem THREW inside checkTourStatus', { error: String(e) });
          }
          const afterWrite = window.localStorage.getItem(tourStorageKey);
          console.log('[TOUR-TELEMETRY] checkTourStatus:setItem verify', {
            tourStorageKey,
            beforeWrite,
            afterWrite,
            persistedOk: afterWrite === '1',
            ts: new Date().toISOString(),
          });
        }
      } catch (error) {
        console.error('Erro ao verificar status do tour:', error);
        console.log('[TOUR-TELEMETRY] checkTourStatus:ERROR', {
          message: error?.message,
          status: error?.response?.status,
          ts: new Date().toISOString(),
        });
      } finally {
        setLoading(false);
      }
    };

    checkTourStatus();
  }, [tourStorageKey]);

  // Marcar tour como completo
  const completeTour = useCallback(async () => {
    // [TOUR-TELEMETRY] completeTour invocation
    const cacheBefore = typeof window !== 'undefined'
      ? window.localStorage.getItem(tourStorageKey)
      : null;
    console.log('[TOUR-TELEMETRY] completeTour:START', {
      tourStorageKey,
      cacheBefore,
      ts: new Date().toISOString(),
    });
    // Marcar imediatamente no cache local — garante que não reabre mesmo se
    // a chamada ao backend falhar (offline, timeout, etc.)
    try { window.localStorage.setItem(tourStorageKey, '1'); } catch (e) {
      console.log('[TOUR-TELEMETRY] completeTour:setItem THREW', { error: String(e) });
    }
    const cacheAfter = typeof window !== 'undefined'
      ? window.localStorage.getItem(tourStorageKey)
      : null;
    console.log('[TOUR-TELEMETRY] completeTour:setItem verify', {
      tourStorageKey,
      cacheBefore,
      cacheAfter,
      persistedOk: cacheAfter === '1',
      ts: new Date().toISOString(),
    });
    try {
      const token = localStorage.getItem('token');
      console.log('[TOUR-TELEMETRY] completeTour:calling-backend POST /onboarding/complete-tour');
      const postRes = await axios.post(`${API}/onboarding/complete-tour`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('[TOUR-TELEMETRY] completeTour:backend-response', {
        status: postRes.status,
        data: postRes.data,
        ts: new Date().toISOString(),
      });
    } catch (error) {
      console.error('Erro ao completar tour:', error);
      console.log('[TOUR-TELEMETRY] completeTour:backend-ERROR', {
        message: error?.message,
        status: error?.response?.status,
        ts: new Date().toISOString(),
      });
    }

    setRun(false);
    if (onComplete) onComplete();
  }, [onComplete, tourStorageKey]);

  // Handler do Joyride
  const handleJoyrideCallback = useCallback((data) => {
    const { action, index, status, type } = data;
    // [TOUR-TELEMETRY] every event the Joyride emits — this proves whether the
    // user's exit path actually triggers completeTour or not.
    console.log('[TOUR-TELEMETRY] Joyride event', {
      action,
      status,
      type,
      index,
      willCallCompleteTour:
        [STATUS.FINISHED, STATUS.SKIPPED].includes(status) ||
        action === ACTIONS.SKIP ||
        action === ACTIONS.CLOSE,
      ts: new Date().toISOString(),
    });

    // Tour finalizado ou pulado
    if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status)) {
      completeTour();
      return;
    }
    
    // Also check for SKIP action (workaround for react-joyride v3 bug)
    if (action === ACTIONS.SKIP) {
      completeTour();
      return;
    }

    // Navegação entre steps
    // react-joyride v3 gerencia o stepIndex internamente quando `continuous` é true.
    // Apenas atualizamos o estado local p/ telemetria/debug, sem passar o prop controlado
    // (caso contrário cria conflito e o tour não avança no clique de "Próximo").
    if (type === EVENTS.STEP_AFTER) {
      if (action === ACTIONS.NEXT) {
        setStepIndex(index + 1);
      } else if (action === ACTIONS.PREV) {
        setStepIndex(index - 1);
      }
    }

    // Clicou fora ou fechou
    if (action === ACTIONS.CLOSE) {
      completeTour();
    }
  }, [completeTour]);

  // Não renderizar se carregando
  if (loading) {
    return null;
  }

  return (
    <Joyride
      steps={steps}
      run={run}
      continuous
      showProgress={false}
      showSkipButton
      hideCloseButton={false}
      disableOverlayClose={false}
      disableScrolling={false}
      spotlightClicks={false}
      callback={handleJoyrideCallback}
      styles={tooltipStyles}
      locale={locale}
      floaterProps={{
        disableAnimation: false,
      }}
      data-testid="onboarding-tour"
    />
  );
}
